/**
 * Defense-in-depth hardening for email HTML rendered via iframe `srcDoc`.
 *
 * Shared by every surface that injects stored email HTML into a frame —
 * `PixelPerfectEmailFrame` (canvas exact-import surface, template
 * detail), the templates gallery card fallback iframe, and the
 * analytics document preview. Client- and server-safe: no React or browser DOM.
 */

import { type DefaultTreeAdapterTypes, parse } from 'parse5'

const FRAME_CSP =
  "script-src 'none'; object-src 'none'; frame-src 'none'; child-src 'none'; " +
  "worker-src 'none'; connect-src 'none'; media-src 'none'; form-action 'none'; " +
  "base-uri 'none'"
const FRAME_CSP_META = `<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">`

/**
 * The frame is a light surface, whatever the viewer's browser prefers. An
 * email's `@media (prefers-color-scheme: dark)` block was written for a
 * dark inbox — Loops, Customer.io and MJML output force white copy in it —
 * and in a dark-mode browser it would paint white text onto the email's
 * own white background (a "blank" email on the flow page). The condition
 * is rewritten so it never matches, and a `color-scheme` that admits dark
 * is pinned to light so the canvas never goes dark under a transparent
 * body. The email then renders as authored for light, which is what its
 * screenshot shows.
 */
const DARK_SCHEME_CONDITION = /\(\s*prefers-color-scheme\s*:\s*dark\s*\)/gi
const NEVER_MATCHING_CONDITION = '(min-width: 1px) and (max-width: 0px)'
const COLOR_SCHEME_DECLARATION =
  /color-scheme\s*:\s*([^;"'}]*\bdark\b[^;"'}]*)/gi
const COLOR_SCHEME_META =
  /(<meta\b[^>]*\bname\s*=\s*["']color-scheme["'][^>]*\bcontent\s*=\s*["'])([^"']*)(["'])/gi

export function pinEmailFrameToLightScheme(html: string): string {
  return html
    .replace(DARK_SCHEME_CONDITION, NEVER_MATCHING_CONDITION)
    .replace(COLOR_SCHEME_DECLARATION, 'color-scheme: light')
    .replace(COLOR_SCHEME_META, '$1light$3')
}

/**
 * Read HTML5 source locations, then patch only the CSP and refresh tags.
 * Serialization would change email pixels; regex matching would mistake
 * comments, raw text or quoted attributes for an effective head or directive.
 * Parsing with scripting disabled also catches refresh inside noscript.
 */
export function hardenEmailFrameHtml(html: string): string {
  const source = pinEmailFrameToLightScheme(html)
  const document = parse(source, {
    sourceCodeLocationInfo: true,
    scriptingEnabled: false,
  })
  const documentType = document.childNodes.find(
    (node) => node.nodeName === '#documentType'
  )
  const root = document.childNodes.find(
    (node): node is DefaultTreeAdapterTypes.Element =>
      'tagName' in node && node.tagName === 'html'
  )
  const head = root?.childNodes.find(
    (node): node is DefaultTreeAdapterTypes.Element =>
      'tagName' in node && node.tagName === 'head'
  )
  const policyOffset =
    head?.sourceCodeLocation?.startTag?.endOffset ??
    documentType?.sourceCodeLocation?.endOffset ??
    0
  const patches: Array<{ start: number; end: number; value: string }> = []
  const pending: Array<DefaultTreeAdapterTypes.Node> = [document]
  while (pending.length > 0) {
    const node = pending.pop()
    if (!node) {
      continue
    }
    if ('childNodes' in node) {
      for (const child of node.childNodes) {
        pending.push(child)
      }
    }
    if (!('tagName' in node)) {
      continue
    }
    const location = node.sourceCodeLocation
    if (
      node.tagName === 'meta' &&
      location &&
      node.attrs.some(
        (attribute) =>
          attribute.name === 'http-equiv' &&
          attribute.value.trim().toLowerCase() === 'refresh'
      )
    ) {
      patches.push({
        start: location.startOffset,
        end: location.endOffset,
        value: '',
      })
    }
  }
  patches.push({
    start: policyOffset,
    end: policyOffset,
    value: FRAME_CSP_META,
  })
  // Apply from the end, removing a tag before inserting at the same offset.
  patches.sort(
    (left, right) => right.start - left.start || right.end - left.end
  )
  return patches.reduce(
    (value, patch) =>
      value.slice(0, patch.start) + patch.value + value.slice(patch.end),
    source
  )
}
